import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Loader2 } from "lucide-react";

const WORKER_URL = "https://contact-form.insorganizasyon.workers.dev/";

export const Contact = () => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const formSchema = z.object({
        name: z.string().min(2, t('contact.validation.name')),
        email: z.string().email(t('contact.validation.email')),
        subject: z.string().min(5, t('contact.validation.subject')),
        message: z.string().min(10, t('contact.validation.message')),
      });

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
          name: "",
          email: "",
          subject: "",
          message: "",
        },
      });

    const onSubmit = async (values: z.infer<typeof formSchema>) => {
        setIsSubmitting(true);
        try {
            const response = await fetch(WORKER_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    name: values.name,
                    email: values.email,
                    subject: values.subject,
                    message: values.message,
                }),
            });

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            toast({
                title: t('contact.toast_success_title'),
                description: t('contact.toast_success_description'),
            });
            form.reset();
        } catch (error) {
            console.error("Failed to send message:", error);
            toast({
                title: t('contact.toast_error_title'),
                description: t('contact.toast_error_description'),
                variant: "destructive",
            });
        } finally {
            setIsSubmitting(false);
        }
    }

  return (
    <section id="contact" className="py-24 bg-primary-50">
      <div className="container mx-auto px-4">
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true, amount: 0.5 }}
            className="text-center md:text-left max-w-3xl mb-14"
        >
          <h2 className="text-3xl md:text-4xl font-bold">{t('contact.title')}</h2>
          <p className="text-lg text-gray-600 mt-4">
            {t('contact.subtitle')}
          </p>
        </motion.div>
        
        <motion.div 
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true, amount: 0.3 }}
            className="max-w-3xl mx-auto bg-white p-8 md:p-12 rounded-2xl shadow-lg"
        >
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input placeholder={t('contact.form.name')} {...form.register("name")} disabled={isSubmitting} />
                <Input placeholder={t('contact.form.email')} {...form.register("email")} disabled={isSubmitting} />
            </div>
            <Input placeholder={t('contact.form.subject')} {...form.register("subject")} disabled={isSubmitting} />
            <Textarea placeholder={t('contact.form.message')} rows={6} {...form.register("message")} disabled={isSubmitting} />
            <Button type="submit" size="lg" className="w-full" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isSubmitting ? t('contact.form.submitting') : t('contact.form.submit')}
            </Button>
          </form>
        </motion.div>
      </div>
    </section>
  );
};
